import { useEffect, useRef } from 'react';
import { useTracker } from '../../state/store';
import { audioEngine } from '../../engine/audio';
import { Button } from './primitives';

/** Master output meter, painted on a canvas so it can update every snapshot. */
function MasterMeter() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const peak = useTracker((s) => s.playback.masterPeak);
  const held = useRef<[number, number]>([0, 0]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = 120;
    const h = 22;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.clearRect(0, 0, w, h);
    const channels: [number, number] = [peak?.[0] ?? 0, peak?.[1] ?? 0];

    for (let i = 0; i < 2; i++) {
      const y = i * 11 + 2;
      const value = channels[i];
      // Peak hold decays slowly so brief transients stay readable.
      held.current[i] = Math.max(value, held.current[i] * 0.94);

      ctx.fillStyle = '#141418';
      ctx.fillRect(0, y, w, 7);

      const width = Math.min(1, value) * w;
      const gradient = ctx.createLinearGradient(0, 0, w, 0);
      gradient.addColorStop(0, '#06c167');
      gradient.addColorStop(0.75, '#4ceb9b');
      gradient.addColorStop(0.92, '#ffb545');
      gradient.addColorStop(1, '#ff5252');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, y, width, 7);

      const holdX = Math.min(1, held.current[i]) * w;
      ctx.fillStyle = held.current[i] > 0.98 ? '#ff5252' : '#e8e8ee';
      ctx.fillRect(Math.max(0, holdX - 1.5), y, 1.5, 7);
    }
  }, [peak]);

  return <canvas ref={canvasRef} style={{ width: 120, height: 22 }} className="rounded" />;
}

export function TransportBar() {
  const playback = useTracker((s) => s.playback);
  const song = useTracker((s) => s.song);
  const currentOrder = useTracker((s) => s.currentOrder);
  const cursorRow = useTracker((s) => s.cursorRow);
  const editMode = useTracker((s) => s.editMode);
  const octave = useTracker((s) => s.octave);
  const editStep = useTracker((s) => s.editStep);
  const followPlayback = useTracker((s) => s.followPlayback);
  const dirty = useTracker((s) => s.dirty);

  const setEditMode = useTracker((s) => s.setEditMode);
  const setOctave = useTracker((s) => s.setOctave);
  const setEditStep = useTracker((s) => s.setEditStep);
  const setFollowPlayback = useTracker((s) => s.setFollowPlayback);

  const play = () => {
    audioEngine.seek(currentOrder, 0);
    audioEngine.play();
  };
  const playFromCursor = () => {
    audioEngine.seek(currentOrder, cursorRow);
    audioEngine.play();
  };
  const stop = () => {
    audioEngine.stop();
    audioEngine.allNotesOff();
  };

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-ink-700 bg-ink-900 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="font-sans text-sm font-bold tracking-tight text-white">
          Fin<span className="text-acid-500">Tracker</span>
        </span>
      </div>

      <div className="flex items-center gap-1">
        <Button onClick={play} tone="accent" title="Play song from the start of this position (Enter)">
          ▶ Play
        </Button>
        <Button onClick={playFromCursor} title="Play from the cursor row">
          ▶ Row
        </Button>
        <Button onClick={() => audioEngine.pause()} title="Pause (Shift+Space)">
          ❚❚
        </Button>
        <Button onClick={stop} title="Stop (Esc)">
          ■
        </Button>
      </div>

      <Button
        onClick={() => setEditMode(!editMode)}
        active={editMode}
        tone={editMode ? 'accent' : 'default'}
        title="Toggle edit mode (Space)"
      >
        {editMode ? '● Record' : '○ Edit'}
      </Button>

      <div className="flex items-center gap-3 font-mono text-[11px] text-ink-400">
        <span title="Order position / pattern">
          POS <span className="text-white">{String(playback.order).padStart(3, '0')}</span>
          <span className="text-ink-500">/</span>
          <span className="text-white">{String(playback.pattern).padStart(2, '0')}</span>
        </span>
        <span title="Row">
          ROW <span className="text-white">{String(playback.row).padStart(2, '0')}</span>
        </span>
        <span title="Ticks per row">
          SPD <span className="text-white">{playback.speed}</span>
        </span>
        <span title="Beats per minute">
          BPM <span className="text-white">{playback.tempo}</span>
        </span>
      </div>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">Oct</span>
        <div className="flex items-center gap-0.5">
          <Button onClick={() => setOctave(octave - 1)} title="Octave down ([)">
            −
          </Button>
          <span className="w-6 text-center font-mono text-xs text-white">{octave}</span>
          <Button onClick={() => setOctave(octave + 1)} title="Octave up (])">
            +
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-400">Step</span>
        <div className="flex items-center gap-0.5">
          <Button onClick={() => setEditStep(editStep - 1)}>−</Button>
          <span className="w-5 text-center font-mono text-xs text-white">{editStep}</span>
          <Button onClick={() => setEditStep(editStep + 1)}>+</Button>
        </div>
      </div>

      <Button
        onClick={() => setFollowPlayback(!followPlayback)}
        active={followPlayback}
        title="Scroll the pattern with playback"
      >
        Follow
      </Button>

      <div className="ml-auto flex items-center gap-3">
        <span className="max-w-[220px] truncate font-mono text-[11px] text-ink-400" title={song.name}>
          {song.name || 'untitled'}
          {dirty && <span className="text-amber-400"> •</span>}
          <span className="ml-2 text-ink-500">
            {song.format.toUpperCase()} · {song.channels}ch
          </span>
        </span>
        <MasterMeter />
      </div>
    </div>
  );
}
